// What stands in a building's way on Step 3: its blockers, each with the line the page
// writes for it from the record's facts. Shared with the edit pane, which shows that line
// beside an override.
import type { AmazonBuilding, BuildingStep } from "../../api/hooks/useAmazonOverview";
import type { StoryStep } from "./blueprint";
import { capitalised, estimateTerms, offeredKwAc, offerTerms } from "./cells";
import { day, daysBetween, formatDate, today } from "./dates";
import { designInputs, heldItems, stillNeeded, whyText } from "./needs";
import { type PathStep, STEPS } from "./pathSteps";
import { isSettled } from "./progress";
import {
  itemNeededBy,
  lateInputs,
  type Links,
  programmeLinks,
  type Scheduled,
} from "./schedule";
import { sizeText } from "./sizes";
import { homeOf } from "./stepRecord";

/** The steps the programme shows: everything on the story except our internal ones. */
const PROGRAMME = STEPS.filter((s) => !s.internal);

export interface Blocker {
  step: PathStep;
  /** ``waiting``: a step still needing items from Amazon behind another blocker. */
  state: "blocked" | "open" | "not_tracked" | "waiting";
  /** Past its needed-by date: for Amazon design inputs, any item it waits for is. */
  late: boolean;
  why: string;
  need: Date | null;
  /** The Step 1 steps that wait on it and are not done yet. */
  holds: string[];
  /** On a box's card, the steps drawn inside the box on Step 1 that are blocked or
   * waiting, each with its own line; the card's state, date and holds cover them all. */
  parts?: Blocker[];
  /** On a box's card, the part whose needed-by date ``need`` is. */
  needOf?: string;
  /** On Step 3's cards, when the programme plans the step to start (its bar's start): the
   * date the card shows and is ordered by (Noaa, 19 Oct 2026). */
  start?: Date | null;
  /** On a box's card, the part whose planned start ``start`` is, which "Why this date"
   * explains. */
  startOf?: string;
  /** A box's card whose own step is done: only steps drawn inside it are still to come. */
  boxDone?: boolean;
}

/** On a freehold building there is no landlord: Amazon Real Estate gives the approval. */
export const forBuilding = (b: AmazonBuilding, programme: PathStep[]): PathStep[] =>
  b.freehold
    ? programme.map((s) =>
        s.id === "llsignoff"
          ? {
              ...s,
              title: "Amazon Real Estate approval",
              owner: "amazon",
              ownerLabel: undefined,
              subs: [
                "Amazon owns the building, so there is no landlord pack",
                "Amazon Real Estate approves the works through its owned-site route, with a named signatory",
              ],
              needs: [],
            }
          : s,
      )
    : programme;

/** The programme's steps as Step 1 shows them: names, owners and lanes as edited there,
 * and none that was removed there. */
export const asOnStepOne = (story: StoryStep[]): PathStep[] => {
  const byId = new Map(story.map((s) => [s.id, s]));
  return PROGRAMME.flatMap((step) => {
    const shown = byId.get(step.id);
    if (!shown) return [step];
    if (shown.hidden) return [];
    const { title, owner, ownerLabel, lane } = shown;
    return [{ ...step, title, owner, ownerLabel, lane }];
  });
};

const waitsOnIn = (links: Links, id: string): string[] => {
  const own = links.get(id);
  return own ? [...own.deps, ...own.endDeps] : [];
};

/** The steps still to come that wait on ``id`` directly, by title, as Step 1 shows them. */
export const holdsUp = (b: AmazonBuilding, story: StoryStep[], id: string): string[] =>
  story
    .filter(
      (x) =>
        !x.hidden && !isSettled(b, x.id) && [...x.deps, ...(x.endDeps ?? [])].includes(id),
    )
    .map((x) => x.title);

/** Every unsettled step a step waits on, directly or further back. */
export const upstreamOf =
  (b: AmazonBuilding, links: Links) =>
  (id: string): Set<string> => {
    const settled = (x: string) => isSettled(b, x);
    const seen = new Set<string>();
    const queue = waitsOnIn(links, id).filter((x) => !settled(x));
    while (queue.length) {
      const up = queue.pop() as string;
      if (seen.has(up)) continue;
      seen.add(up);
      queue.push(...waitsOnIn(links, up).filter((x) => !settled(x)));
    }
    return seen;
  };

/** The blockers ``id`` waits behind, directly or further back: the chain roots on the
 * Blockers list that hold it up, so a milestone and the cards never disagree. */
export const blockersBehind = (
  b: AmazonBuilding,
  links: Links,
  blockers: Blocker[],
  id: string,
): Blocker[] => {
  const up = upstreamOf(b, links)(id);
  return blockers.filter((x) => up.has(x.step.id));
};

/** The requirements still open from feedback on ``id``'s asks, by who owes them: "Open
 * feedback: 4 requirements, all on Amazon". The page never says "point" (Noaa, 25 Oct 2026). */
const openFeedback = (b: AmazonBuilding, id: string): string => {
  const points = b.steps[id]?.open_points ?? [];
  if (!points.length) return "";
  const byWho = new Map<string, number>();
  for (const p of points) {
    const who = p.owed_by || "no one on record";
    byWho.set(who, (byWho.get(who) ?? 0) + 1);
  }
  const owed =
    byWho.size === 1
      ? `${points.length === 1 ? "" : "all "}on ${[...byWho.keys()][0]}`
      : [...byWho].map(([who, n]) => `${n} on ${who}`).join(", ");
  return `Open feedback: ${points.length} ${points.length === 1 ? "requirement" : "requirements"}, ${owed}.`;
};

/** Why ``id`` is at risk, in plain sentences (Noaa, 19 Oct 2026): first the step's own
 * items that hold it up, each with its date and reason, and the open feedback on its
 * asks; then each chain root on the Blockers list it waits behind that is past its
 * needed-by date, or owed by Amazon with no date given. A step simply under way on time
 * is no risk, though everything after it waits on it, and open feedback alone does not
 * make it one. ``need`` dates the step's own items. */
export const riskCauses = (
  b: AmazonBuilding,
  links: Links,
  blockers: Blocker[],
  id: string,
  now: Date,
  need: Record<string, Date> | null = null,
): string[] => {
  // Requirements open from feedback are summed up once below (openFeedback).
  const own = heldItems(b, id)
    .filter((n) => !n.design_input && !n.point_from)
    .map((n) => {
      const by = itemNeededBy(b, n, need);
      const late =
        by && by < now ? `, needed by ${formatDate(by)}, ${daysBetween(by, now)} days ago` : "";
      const why = whyText(n);
      return `Held up by ${n.text}${late}.${why ? ` Why: ${why}.` : ""}`;
    });
  const behind = blockersBehind(b, links, blockers, id).flatMap((x) => {
    const s = b.steps[x.step.id];
    const late = x.need && x.need < now ? daysBetween(x.need, now) : 0;
    const undated =
      x.step.owner === "amazon" && x.state === "open" && !s.due && s.waits_on !== "us";
    if (!late && !undated) return [];
    const was = late && x.need ? `needed by ${formatDate(x.need)}, ${late} days ago.` : "";
    const name = own.length ? `Also waiting on ${x.step.title}` : x.step.title;
    // The forecast plans on the inputs arriving tomorrow (Noaa, 18 Oct): name them.
    const outstanding = x.step.id === "inputs" ? designInputs(b).missing.length : 0;
    if (!outstanding) {
      const noDate = undated ? `${was ? " " : ""}Amazon has given no date.` : "";
      return [`${name}: ${capitalised(was)}${noDate}`];
    }
    return [
      `${name}: ${outstanding} not received.${was ? ` They were ${was}` : ""} ` +
        (undated
          ? "Amazon has given no date, so this plan assumes they arrive tomorrow."
          : "This plan assumes they arrive tomorrow."),
    ];
  });
  if (!own.length && !behind.length) return [];
  const feedback = openFeedback(b, id);
  return [...own, ...(feedback ? [feedback] : []), ...behind];
};

/** What stands between the building and its next steps, at the root of each chain only:
 * anything blocked or under way, and any step the platform cannot see whose steps before
 * it are done, unless a step it waits on (directly or further back) is itself one of
 * these. The links are Step 1's as edited there, read the way the programme reads them
 * (``programmeLinks``). Each blocker names the Step 1 steps that wait on it. */
export const blockersFor = (
  b: AmazonBuilding,
  need: Record<string, Date> | null,
  links: Links,
  programme: PathStep[],
  story: StoryStep[],
  now: Date,
): Blocker[] => {
  const settled = (id: string) => isSettled(b, id);
  const candidate = (step: PathStep): boolean => {
    const s = b.steps[step.id];
    if (!s) return false;
    return (
      s.state === "blocked" ||
      s.state === "open" ||
      (s.state === "not_tracked" && waitsOnIn(links, step.id).every(settled))
    );
  };
  const candidates = new Set(programme.filter(candidate).map((s) => s.id));
  const upstream = upstreamOf(b, links);
  // Held behind another blocker, unless the two wait on each other (links edited into a
  // loop): then neither is behind the other, and both stay listed.
  const heldBehindAnother = (id: string): boolean =>
    [...upstream(id)].some((up) => up !== id && candidates.has(up) && !upstream(up).has(id));
  const roots = programme.filter(
    (step) => candidates.has(step.id) && !heldBehindAnother(step.id),
  );
  const list: Blocker[] = roots.map((step) => {
    const s = b.steps[step.id];
    const by = need?.[step.id] ?? null;
    return {
      step,
      state: s.state as Blocker["state"],
      late:
        step.id === "inputs"
          ? lateInputs(b, need, now).length > 0
          : s.state !== "not_tracked" && ((by !== null && by < now) || ownDuePassed(s, now)),
      why: cardLine(b, step),
      need: by,
      holds: holdsUp(b, story, step.id),
    };
  });
  // Blocked and late cards first, so late design inputs stay near the top.
  const urgent = (x: Blocker) => Number(x.state === "blocked" || x.late);
  return list.sort(
    (x, y) =>
      urgent(y) - urgent(x) ||
      (x.need?.getTime() ?? Infinity) - (y.need?.getTime() ?? Infinity),
  );
};

/** Cards planned to start the same day, in the order Noaa reads them (19 Oct 2026); any
 * other step after these. */
const SAME_START_ORDER = ["signoff", "llpack", "roof", "inputs", "design", "access2"];
const sameStartRank = (id: string): number => {
  const at = SAME_START_ORDER.indexOf(id);
  return at < 0 ? SAME_START_ORDER.length : at;
};

/** The cards Step 3 lists: each blocker, and every other step still needing an item from
 * Amazon, named after the blockers ahead of it, so each item sits on the card for its own
 * step (Noaa, 18 Oct). Soonest planned start first (``plan``, the programme's bars; Noaa,
 * 19 Oct 2026), then on the same start in Noaa's order (``SAME_START_ORDER``), then
 * soonest needed, then blocked; the undated last. ``blockers`` stays the
 * chain roots for the verdict and the milestones' risks. */
export const blockerCards = (
  b: AmazonBuilding,
  blockers: Blocker[],
  need: Record<string, Date> | null,
  plan: Record<string, Scheduled> | null,
  links: Links,
  programme: PathStep[],
  story: StoryStep[],
  now: Date,
): Blocker[] => {
  const ids = new Set(blockers.map((x) => x.step.id));
  const inputsIsCard = ids.has("inputs");
  // A design input is listed on Amazon design inputs while that is a card, but the step it
  // is for (Detailed design, Roof inspection) still waits on it, so keeps a card of its own.
  const homes = new Set(
    (b.needs ?? [])
      .filter((n) => stillNeeded(b, n))
      .flatMap((n) => [homeOf(n, inputsIsCard), n.step]),
  );
  const behind: Blocker[] = programme
    .filter((step) => homes.has(step.id) && !ids.has(step.id) && b.steps[step.id])
    .map((step) => {
      const s = b.steps[step.id];
      const ahead = blockersBehind(b, links, blockers, step.id).map((x) => x.step.title);
      const by = need?.[step.id] ?? null;
      return {
        step,
        state: s.state === "blocked" || s.state === "open" ? s.state : "waiting",
        // Late only once under way: a step still waiting on earlier ones says so.
        late: s.state === "open" && ((by !== null && by < now) || ownDuePassed(s, now)),
        why: [ahead.length ? `After ${ahead.join(", ")}` : "", cardLine(b, step)]
          .filter(Boolean)
          .join(" · "),
        need: by,
        holds: holdsUp(b, story, step.id),
      };
    });
  const started = [...blockers, ...behind].map((x) => ({
    ...x,
    start: plan?.[x.step.id]?.start ?? null,
  }));
  return byBox(started, programme, b, story).sort(
    (x, y) =>
      (x.start?.getTime() ?? Infinity) - (y.start?.getTime() ?? Infinity) ||
      sameStartRank(x.step.id) - sameStartRank(y.step.id) ||
      (x.need?.getTime() ?? Infinity) - (y.need?.getTime() ?? Infinity) ||
      Number(y.state === "blocked") - Number(x.state === "blocked"),
  );
};

/** Worst first: what a box's card reads when its parts differ. */
const STATE_RANK: Record<Blocker["state"], number> = {
  blocked: 0,
  open: 1,
  not_tracked: 2,
  waiting: 3,
};

/** One card per Step 1 box (Noaa, 18 Oct): a step drawn inside another's box (Landlord
 * sign-off in Design approval's) is listed on that box's card, never on a card of its own.
 * A box Step 1 leaves out keeps its parts' cards as they are. */
const byBox = (
  cards: Blocker[],
  programme: PathStep[],
  b: AmazonBuilding,
  story: StoryStep[],
): Blocker[] => {
  const boxOf = (x: Blocker) => {
    const box = x.step.inBox && programme.find((s) => s.id === x.step.inBox);
    return box || x.step;
  };
  const groups = new Map<string, { box: PathStep; cards: Blocker[] }>();
  for (const x of cards) {
    const box = boxOf(x);
    const group = groups.get(box.id) ?? { box, cards: [] };
    group.cards.push(x);
    groups.set(box.id, group);
  }
  return [...groups.values()].map(({ box, cards: group }) => {
    const own = group.find((x) => x.step.id === box.id);
    const parts = group.filter((x) => x !== own);
    if (!parts.length && own) return own;
    const dated = group.filter((x) => x.need).sort((x, y) => +(x.need as Date) - +(y.need as Date));
    const starting = group
      .filter((x) => x.start)
      .sort((x, y) => +(x.start as Date) - +(y.start as Date));
    const inside = new Set(
      programme.filter((s) => s.id === box.id || s.inBox === box.id).map((s) => s.title),
    );
    return {
      step: box,
      state: group.map((x) => x.state).sort((x, y) => STATE_RANK[x] - STATE_RANK[y])[0],
      late: group.some((x) => x.late),
      why: own?.why ?? "",
      need: dated[0]?.need ?? null,
      needOf: dated[0]?.step.id,
      start: starting[0]?.start ?? null,
      startOf: starting[0]?.step.id,
      boxDone: !own && isSettled(b, box.id),
      holds: [
        ...new Set([...holdsUp(b, story, box.id), ...group.flatMap((x) => x.holds)]),
      ].filter((title) => !inside.has(title)),
      parts,
    };
  });
};

/** The step's own due date, one its owner gave rather than our estimate, has passed with
 * the step not done: it reads "was due", and its card is Late, as Step 2's cell is. */
const ownDuePassed = (s: BuildingStep, now: Date): boolean =>
  Boolean(s.due) && !s.estimated && s.state !== "done" && day(s.due) < now;

const kwAcText = (kw: number | null | undefined): string =>
  kw ? sizeText(kw, "kWac") : "";

/** The line under a blocker's name, from the record's facts in wording set here, never
 * an agent's free text: what Amazon reads on this page is what this function writes. It
 * never says whom the card waits on: the items listed under it do (Noaa, 21 Oct 2026). */
export const cardLine = (b: AmazonBuilding, step: PathStep): string => {
  const s = b.steps[step.id];
  const app = b.steps.gridapp;
  const applied = [
    app?.date ? `Applied ${formatDate(day(app.date))}` : "",
    kwAcText(app?.kw_ac) ? `for ${kwAcText(app?.kw_ac)}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const due = s.due
    ? s.estimated
      ? `estimated ${formatDate(day(s.due))}`
      : ownDuePassed(s, today())
        ? `was due ${formatDate(day(s.due))}`
        : `expected by ${formatDate(day(s.due))}`
    : "";
  if (step.id === "gridoffer") {
    const terms = offerTerms(b);
    if (terms !== null) return terms;
    const estimate = estimateTerms(b);
    if (estimate !== null) return `${estimate} · a formal offer needs a new application`;
    const answer = due ? `DNO offer ${due}` : "No date from the DNO yet";
    return [applied, answer].filter(Boolean).join(" · ");
  }
  if (step.id === "gridacc") {
    const offer = kwAcText(offeredKwAc(b));
    const received = b.steps.gridoffer?.date;
    return [
      received ? `Offer received ${formatDate(day(received))}` : "Offer in",
      offer ? `allows ${offer}` : "",
      s.due ? `accept by ${formatDate(day(s.due))}` : "",
    ]
      .filter(Boolean)
      .join(" · ");
  }
  // Nothing on record says it happened; only what the record notes about it, such as
  // Design readiness finding no design that matches the application.
  if (s.state === "not_tracked" && s.note) return capitalised(s.note);
  // The preliminary pack's later stages show only once the stage before is done, so their
  // line says how far it got and whose move is next: Amazon's asset manager sends the pack
  // on (Step 1's "Sent to landlord"), then the landlord comes back (Noaa, 19 Oct 2026).
  if (s.state === "not_tracked" && (step.id === "llsent" || step.id === "llfeedback")) {
    const before = step.id === "llsent" ? "llpack" : "llsent";
    const on = b.steps[before]?.date;
    const when = on ? ` ${formatDate(day(on))}` : "";
    return step.id === "llsent"
      ? `Sent to Amazon${when}; next, Amazon sends it on to the landlord, or tells us it has`
      : `Sent to the landlord${when}; next, the landlord's feedback`;
  }
  if (s.state === "not_tracked") return "";
  return [step.id === "inputs" ? s.detail : "", due && capitalised(due)]
    .filter(Boolean)
    .join(" · ");
};

export const STATE_LABEL: Record<Blocker["state"] | "late", string> = {
  blocked: "Blocked",
  late: "Late",
  open: "In progress",
  not_tracked: "Not happened yet",
  waiting: "Waiting on earlier steps",
};

/** A card's state as its label reads it: blocked, else late, else as it stands. */
export const cardState = (x: Blocker): keyof typeof STATE_LABEL =>
  x.state === "blocked" ? "blocked" : x.late ? "late" : x.state;

export const OWNER_NAME = {
  inrange: "InRange",
  amazon: "Amazon",
  third: "third party",
  both: "both of us",
};

/** The programme's steps as Step 3 shows them for ``b``. */
export const programmeFor = (b: AmazonBuilding, story: StoryStep[]): PathStep[] =>
  forBuilding(b, asOnStepOne(story));

/** Whether Step 3 lists ``id`` on a card of its own or inside its box's card, as the
 * building view lists them. The needed-by dates only order and date the cards, so none
 * are worked out here. */
export const onStepThree = (b: AmazonBuilding, story: StoryStep[], id: string): boolean => {
  const programme = programmeFor(b, story);
  const links = programmeLinks(story);
  const now = today();
  const blockers = blockersFor(b, null, links, programme, story, now);
  return blockerCards(b, blockers, null, null, links, programme, story, now).some(
    (x) => x.step.id === id || (x.parts ?? []).some((p) => p.step.id === id),
  );
};

/** When Step 3 has a card for a step, for the edit pane to say while it has none. */
export const STEP_THREE_SHOWS =
  "Step 3 shows a card for this step while it is blocked or in progress, or is next with every step before it done, and no earlier card on Step 3 holds it up; or while an item for it is still needed from Amazon.";
